import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
// Side-effect import: configures the pdf.js worker once before any <Document>
// mounts (see pdfSetup.js).
import "./pdfSetup";
// react-pdf's text/annotation layers need their CSS or the build/runtime warns
// and the layers render unstyled (overlapping selectable text). Importing them
// keeps the layers usable; alternatively pass renderTextLayer={false} below.
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

const clampPage = (page, total) => {
  if (!total || total < 1) return 1;
  return Math.min(Math.max(page, 1), total);
};

// Paged PDF reader for UPLOADED files (same-origin file_url). Initializes the
// page from `initialPage` (the teacher's saved resume point), clamps it to the
// document once the page count is known, and reports every page change up via
// `onPageChange` (throttle/persist is the caller's job). On a load error it
// renders `fallback` so the detail page can fall back to its open-original
// affordance.
export default function PdfReader({
  file,
  title,
  initialPage = 1,
  onPageChange,
  fallback = null,
}) {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(initialPage > 0 ? initialPage : 1);
  const [failed, setFailed] = useState(false);

  // Mirror the live page in a ref so the unmount effect can report the final
  // page without re-subscribing every render.
  const pageRef = useRef(page);
  pageRef.current = page;
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  // Guards the unmount flush: only write back a page once the document has
  // actually loaded, so leaving before load never persists the raw (possibly
  // stale/out-of-range) initialPage.
  const loadedRef = useRef(false);

  const goToPage = useCallback(
    (next) => {
      setPage((current) => {
        const target = clampPage(next, numPages);
        if (target !== current) onPageChangeRef.current?.(target);
        return target;
      });
    },
    [numPages]
  );

  const handleLoadSuccess = useCallback(({ numPages: total }) => {
    loadedRef.current = true;
    setNumPages(total);
    // Clamp the restored page to the real document length (a stale resume
    // point past the end snaps back to the last page).
    setPage((current) => clampPage(current, total));
  }, []);

  const handleLoadError = useCallback(() => setFailed(true), []);

  // On unmount, persist wherever the teacher stopped reading — but only if the
  // document loaded, so we never write back the unvalidated initialPage.
  useEffect(() => {
    return () => {
      if (loadedRef.current) onPageChangeRef.current?.(pageRef.current);
    };
  }, []);

  if (failed) return fallback;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => goToPage(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:text-indigo-600 hover:border-indigo-200 transition disabled:opacity-40 disabled:hover:text-slate-600 disabled:hover:border-slate-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M15 19l-7-7 7-7" />
          </svg>
          {t("materials.pdfPrev")}
        </button>
        <span className="text-sm font-semibold text-slate-600 tabular-nums">
          {numPages
            ? t("materials.pdfPage", { page, total: numPages })
            : t("materials.pdfLoading")}
        </span>
        <button
          type="button"
          onClick={() => goToPage(page + 1)}
          disabled={!numPages || page >= numPages}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:text-indigo-600 hover:border-indigo-200 transition disabled:opacity-40 disabled:hover:text-slate-600 disabled:hover:border-slate-200"
        >
          {t("materials.pdfNext")}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="flex justify-center rounded-2xl border border-slate-200 bg-slate-100 p-3 overflow-auto max-h-[80vh]">
        <Document
          file={file}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          onSourceError={handleLoadError}
          loading={
            <div className="py-24 text-sm font-semibold text-slate-400">
              {t("materials.pdfLoading")}
            </div>
          }
          error={null}
        >
          <Page
            pageNumber={page}
            className="shadow-md"
            renderAnnotationLayer
            renderTextLayer
          />
        </Document>
      </div>
    </div>
  );
}
